import React, { Fragment, useState, useContext, useCallback } from "react";
import {connect} from "react-redux";
import {injectIntl} from "react-intl";

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import UIPopupContext from "../../popups/UIPopup/UIPopupContext";
import {updatePromptParts, getCurrentPromptParts} from "../aiApi";
import LoadingSpinner from "../../ui/loadingSpinner";
import TextInput from "../../ui/textInput";
import {hideGroupObject} from "../aiFunctions";
import UiPopupAIContentEditingModal from "./UiPopupAIContentEditingModal";
import {useAppDispatch, useAppSelector} from "../../../storeHooks";
import {setUiPopupState} from "../../../actions/actionFunctions";

const UiPopupAIContentFeatureGroups = (props) => {

  const {type} = props;

  const dispatch = useAppDispatch();
  const {uiPopupState} = useAppSelector(state => state.messageBoxState);

  const {uiPopupContext, uiPopupDispatch} = useContext(UIPopupContext);
  const {groupObjects, promptsList, showAllFeatureGroups} = uiPopupContext;

  const [editingGroupObject, setEditingGroupObject] = useState(null);

  const isActiveType = "isActive" + type;
  const isActiveBody = "partBody" + type;
  const promptsPartsLoading = "promptsPartsLoading" + type;
  const savedPromptPartObject = "savedPromptPartObject" + type;

  const onCheckboxClick = useCallback((gr, property = "enabled") => {
    const newValue = !gr[property];
    uiPopupDispatch({
      groupObjects: groupObjects.map((groupObject) => {
        if (groupObject.partId === gr.partId) {
          return {...groupObject, [property]: newValue};
        }
        return groupObject;
      }),
    });
    for (const numText of ["First", "Second", "Third"]) {
      const forType = "isActive" + numText;
      const activePrompt = promptsList.find((p) => p[forType]);
      if (!activePrompt) return;
      updatePromptParts(
        { ...gr, [property]: newValue },
        activePrompt,
        numText
      );
    }
  }, [groupObjects, promptsList, uiPopupDispatch]);

  const handleOpenEditor = (groupObject) => {
    dispatch(setUiPopupState({
      ...uiPopupState,
      style: {maxWidth: "98%", maxHeight: "95%"}
    }));
    setEditingGroupObject(groupObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const handleCloseEditor = async (newValue) => {
    const groupObject = editingGroupObject;
    setEditingGroupObject(null);

    dispatch(setUiPopupState({
      ...uiPopupState,
      style: {maxWidth: "98%", maxHeight: "95%"}
    }));

    if (!groupObject) return;

    // 1) update value in context
    uiPopupDispatch({
      groupObjects: groupObjects.map((gr) => {
        if (gr.partId === groupObject.partId) {
          return { ...gr, [isActiveBody]: newValue };
        }
        return gr;
      }),
    });

    // 2) persist (same logic you had onBlur)
    const activePrompt = promptsList.find((p) => p[isActiveType]);
    if (!activePrompt) return;

    // updatePromptParts expects the updated body on the groupObject param,
    // so pass a patched copy (safer than relying on async state updates)
    const wasCreate = !groupObject[savedPromptPartObject];
    await updatePromptParts(
      { ...groupObject, [isActiveBody]: newValue },
      activePrompt,
      type
    );

    // api/PromptParts/list (create) returns nothing, so after a create we
    // re-fetch the parts and store the newly created one back into context.
    // Otherwise the next edit before a reload would not find
    // groupObject[savedPromptPartObject] and would create a duplicate instead
    // of updating it. (Same matching logic as UiPopupAIContentPrompts.js.)
    if (wasCreate) {
      try {
        const prPartsResponse = await getCurrentPromptParts(activePrompt);
        const fgName = groupObject.featureGroup.dbName || groupObject.featureGroup.partName;
        const createdPart =
          prPartsResponse.find((prPart) => prPart.featureGroup === fgName && prPart.partBody) ||
          prPartsResponse.find((prPart) => prPart.featureGroup === fgName) ||
          null;
        if (createdPart) {
          uiPopupDispatch((state) => ({
            ...state,
            groupObjects: (state.groupObjects || []).map((gr) => {
              if (gr.partId === groupObject.partId) {
                return {
                  ...gr,
                  [isActiveBody]: createdPart.partBody,
                  [savedPromptPartObject]: createdPart,
                  sendThumbnail: createdPart.sendThumbnail,
                  enabled: createdPart.enabled,
                };
              }
              return gr;
            }),
          }));
        }
      } catch (e) {
        console.log("handleCloseEditor: failed to refetch created prompt part", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  return (
    <Fragment>
      <ul className="ass-list u-pr-1 u-w-full">
        {groupObjects
          ? groupObjects.map((groupObject) => {
            if (hideGroupObject(groupObject, showAllFeatureGroups)) return null;

            return (
              <li key={groupObject.featureGroup.partId} className="ass-list__item">
                <FormControlLabel
                  style={{ width: "160px", flex: "0 0 160px", lineHeight: 1 }}
                  className="ll-text bold color-primary text-left"
                  label={groupObject.featureGroup.dbName || groupObject.featureGroup.partName}
                  control={
                    <Checkbox
                      checked={groupObjects.some(
                        (gr) => gr.partId === groupObject.partId && gr.enabled
                      )}
                      onChange={() => onCheckboxClick(groupObject)}
                    />
                  }
                />

                {uiPopupContext[promptsPartsLoading] ? (
                  <LoadingSpinner size=" message-loading-left-panel-spinner message-loading-left-panel-spinner--30 u-w-3" />
                ) : (
                  <TextInput
                    className={"ui-input ui-input--new ui-input--gray"}
                    name={groupObject.partId}
                    value={groupObject[isActiveBody] || ""}
                    // open modal on focus/click
                    onFocus={() => handleOpenEditor(groupObject)}
                    onClick={() => handleOpenEditor(groupObject)}
                    onChange={() => null}
                    // prevent typing in the tiny input
                    readOnly
                  />
                )}
                <FormControlLabel
                  style={{ width: "180px", flex: "0 0 180px", margin: "0 0 0 16px", height: "40px" }}
                  className="ll-text bold color-primary text-left"
                  label="Send Thumbnail"
                  control={
                    <Checkbox
                      checked={!groupObjects.some(
                        (gr) => gr.partId === groupObject.partId && gr.sendThumbnail === false
                      )}
                      onChange={() => onCheckboxClick(groupObject, "sendThumbnail")}
                    />
                  }
                />
              </li>
            );
          })
          : null}
      </ul>

      <UiPopupAIContentEditingModal
        propsOpened={!!editingGroupObject}
        propsValue={editingGroupObject?.[isActiveBody] || ""}
        propsTitle={
          editingGroupObject?.featureGroup?.dbName || editingGroupObject?.featureGroup?.partName || ""
        }
        propsClose={handleCloseEditor}
      />
    </Fragment>
  );
};

const mapStateToProps = (store) => {
  const fabricatorData = store.fabricatorState;

  return {
    fabricatorData,
  };
};

export default injectIntl(
  connect(mapStateToProps)(UiPopupAIContentFeatureGroups)
)

